/**
 * Cámara + detección de pose en un bucle propio, separado del render del juego.
 *
 * - getUserMedia (cámara frontal / webcam), sin audio. La imagen nunca se guarda ni se envía.
 * - Cada fotograma nuevo de la cámara (requestVideoFrameCallback) se pasa al Web Worker como
 *   ImageBitmap, como mucho a `targetFps` y con un solo fotograma en vuelo: si la inferencia va
 *   lenta se saltan fotogramas en vez de acumular retraso.
 * - Si el worker no puede usar la GPU, la detección se hace en el hilo principal.
 *
 * Cada resultado llega a onPose({ keypoints, aspect, t, captureTime, inferMs, pipelineMs }):
 * keypoints ya en espejo y en alturas de imagen (lo que espera GestureDetector), o null.
 */
const now = () => performance.now();
// Valores admitidos de las opciones que pueden venir de la dirección (?model=…): nunca se usa
// texto arbitrario de la URL (se acabaría mostrando en la página).
const MODELS = ['lightning', 'thunder'];
const BACKENDS = ['auto', 'webgpu', 'webgl', 'wasm'];

export class PoseTracker {
  constructor({ model = 'lightning', backend = 'auto', targetFps = 30, useWorker = true, threads = -1 } = {}) {
    if (!MODELS.includes(model)) model = 'lightning';
    if (!BACKENDS.includes(backend)) backend = 'auto';
    threads = Number.isInteger(threads) ? Math.max(-1, Math.min(8, threads)) : -1;
    this.opts = { model, backend, targetFps, useWorker, threads };
    this.video = null;
    this.stream = null;
    this.worker = null;
    this.engine = null; // motor en el hilo principal (alternativa)
    this.onPose = null;
    this.onError = null;
    this.running = false;
    this.busy = false;
    this.lastSent = 0;
    this.frameId = 0;
    this.pending = new Map();
    this.info = { backend: '-', model, where: '-' };
    this.stats = { fps: 0, inferMs: 0, pipelineMs: 0, dropped: 0 };
    this.brightness = 1;
    this.lastResultAt = 0;
    this.lumaCanvas = null;
  }

  /** Pide la cámara y prepara el modelo. Lanza un Error con mensaje en español si algo falla. */
  async start(video) {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error(window.isSecureContext
        ? 'Este navegador no permite usar la cámara.'
        : 'La cámara necesita HTTPS (o localhost). Abre el juego con la dirección https://.');
    }
    this.video = video;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
      });
    } catch (e) {
      throw new Error(e.name === 'NotAllowedError'
        ? 'Has denegado el permiso de la cámara. Actívalo en los ajustes del navegador para este sitio.'
        : e.name === 'NotFoundError' ? 'No se encuentra ninguna cámara.' : `No se pudo abrir la cámara (${e.name}).`);
    }
    video.srcObject = this.stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();
    await this.#initEngine();
    this.running = true;
    this.#schedule();
  }

  /** Cambia de modelo (Lightning ↔ Thunder) sin cerrar la cámara. */
  async setModel(model) {
    if (!MODELS.includes(model)) return;
    this.opts.model = model;
    this.running = false;
    await this.#initEngine();
    this.running = true;
    this.#schedule();
  }

  stop() {
    this.running = false;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.worker?.terminate();
    this.worker = null;
    this.engine?.dispose();
    this.engine = null;
    if (this.video) this.video.srcObject = null;
  }

  /** Direcciones que han pedido la página y el worker (auditoría de privacidad). */
  async resourceUrls() {
    const urls = performance.getEntriesByType('resource').map((e) => e.name);
    if (this.worker) {
      const w = await new Promise((resolve) => {
        const onMsg = ({ data }) => {
          if (data.type !== 'resources') return;
          this.worker.removeEventListener('message', onMsg);
          resolve(data.urls);
        };
        this.worker.addEventListener('message', onMsg);
        this.worker.postMessage({ type: 'resources' });
        setTimeout(() => resolve([]), 1000);
      });
      urls.push(...w);
    }
    return urls;
  }

  async #initEngine() {
    const { model, backend, useWorker, threads } = this.opts;
    const modelBase = new URL('models/movenet/', document.baseURI).href;
    this.busy = false;
    this.pending.clear();
    this.results = 0;
    this.worker?.terminate();
    this.worker = null;
    this.engine?.dispose();
    this.engine = null;

    if (useWorker && typeof OffscreenCanvas !== 'undefined') {
      try {
        const worker = new Worker(new URL('./pose.worker.js', import.meta.url), { type: 'module' });
        const ready = await new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('el worker no respondió')), 30000);
          worker.onmessage = ({ data }) => {
            if (data.type === 'ready') {
              clearTimeout(timer);
              resolve(data);
            } else if (data.type === 'error') {
              clearTimeout(timer);
              reject(new Error(data.message));
            }
          };
          worker.onerror = (e) => {
            clearTimeout(timer);
            reject(new Error(e.message || 'error en el worker'));
          };
          worker.postMessage({ type: 'init', model, backend, modelBase, threads });
        });
        worker.onmessage = ({ data }) => this.#onWorkerMessage(data);
        this.worker = worker;
        this.info = { backend: ready.backend, cpu: ready.cpu, model, where: 'worker' };
        return;
      } catch (e) {
        console.warn('[pose] worker sin GPU, se usa el hilo principal:', e.message);
      }
    }
    const { createEngine } = await import('./engine.js');
    this.engine = await createEngine({ model, backend, modelBase, threads });
    this.info = { backend: this.engine.backend, cpu: this.engine.cpu, model, where: 'hilo principal' };
  }

  #schedule() {
    const v = this.video;
    if (!this.running || !v) return;
    if (v.requestVideoFrameCallback) v.requestVideoFrameCallback((t, meta) => this.#onFrame(meta.captureTime ?? t));
    else requestAnimationFrame((t) => this.#onFrame(t));
  }

  async #onFrame(captureTime) {
    if (!this.running) return;
    this.#schedule();
    const t = now();
    if (this.busy) {
      this.stats.dropped++;
      return;
    }
    if (t - this.lastSent < 1000 / this.opts.targetFps - 4) return;
    this.lastSent = t;
    this.#sampleBrightness(t);
    this.busy = true;
    const id = ++this.frameId;
    try {
      if (this.worker) {
        const bitmap = await createImageBitmap(this.video);
        this.pending.set(id, captureTime);
        this.worker.postMessage({ type: 'frame', id, bitmap }, [bitmap]);
      } else {
        const t0 = now();
        const keypoints = await this.engine.detect(this.video);
        this.#deliver(keypoints, this.video.videoWidth, this.video.videoHeight, now() - t0, captureTime);
      }
    } catch (e) {
      this.busy = false;
      this.onError?.(e);
    }
  }

  #onWorkerMessage(data) {
    if (data.type === 'pose') {
      const captureTime = this.pending.get(data.id) ?? now();
      this.pending.delete(data.id);
      this.#deliver(data.keypoints, data.width, data.height, data.inferMs, captureTime);
    } else if (data.type === 'error') {
      this.pending.delete(data.id);
      this.busy = false;
      this.onError?.(new Error(data.message));
    }
  }

  #deliver(raw, width, height, inferMs, captureTime) {
    this.busy = false;
    const t = now();
    let keypoints = null;
    if (raw) {
      // Espejo + alturas de imagen: x' = (ancho − x) / alto, y' = y / alto.
      keypoints = new Float32Array(51);
      for (let i = 0; i < 17; i++) {
        keypoints[i * 3] = (width - raw[i * 3]) / height;
        keypoints[i * 3 + 1] = raw[i * 3 + 1] / height;
        keypoints[i * 3 + 2] = raw[i * 3 + 2];
      }
    }
    const s = this.stats;
    const pipelineMs = t - captureTime;
    const dt = this.lastResultAt ? t - this.lastResultAt : 0;
    this.lastResultAt = t;
    const ema = (old, v) => (old ? old * 0.9 + v * 0.1 : v);
    // Las primeras inferencias compilan los shaders del modelo (segundos): no cuentan en las medias.
    if (++this.results > 3) {
      if (dt > 0) s.fps = ema(s.fps, 1000 / dt);
      s.inferMs = ema(s.inferMs, inferMs);
      s.pipelineMs = ema(s.pipelineMs, pipelineMs);
    }
    this.onPose?.({ keypoints, aspect: width / height, t: captureTime / 1000, captureTime, inferMs, pipelineMs });
  }

  /** Brillo medio de la imagen (para el aviso «falta luz»). Dos veces por segundo basta. */
  #sampleBrightness(t) {
    if (t - (this.lumaAt ?? 0) < 500) return;
    this.lumaAt = t;
    if (!this.lumaCanvas) {
      this.lumaCanvas = document.createElement('canvas');
      this.lumaCanvas.width = 32;
      this.lumaCanvas.height = 24;
      this.lumaCtx = this.lumaCanvas.getContext('2d', { willReadFrequently: true });
    }
    this.lumaCtx.drawImage(this.video, 0, 0, 32, 24);
    const d = this.lumaCtx.getImageData(0, 0, 32, 24).data;
    let sum = 0;
    for (let i = 0; i < d.length; i += 4) sum += d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
    this.brightness = sum / (d.length / 4) / 255;
  }
}
